import { useCallback, useEffect, useRef, useState } from 'react';
import { AuthContextProps } from 'react-oidc-context';
import {
  diarizeAudio,
  mintLiveToken,
  submitTranscriptSegment,
  TranscriptIngestResponse,
} from '../utils/backend-utils';
import {
  TARGET_SAMPLE_RATE,
  arrayBufferToBase64,
  concatArrayBuffers,
  downsampleBuffer,
  floatTo16BitPCM,
} from '../utils/audioPcm';

export interface LiveTranscriptLine {
  id: string;
  text: string;
  speakerLabel: string;
  isFinal: boolean;
  startedAt?: string;
  endedAt?: string;
}

interface UseLiveTranscriptionOptions {
  sessionId: string | null;
  auth: AuthContextProps;
  enabled: boolean;
  onSegmentSubmitted?: (response: TranscriptIngestResponse) => void;
  onError?: (message: string) => void;
}

const DIARIZE_INTERVAL_MS = 45000;
const PCM_RING_MAX_SECONDS = 60;
const LIVE_SETUP_TIMEOUT_MS = 30000;
const MAX_WS_RECONNECT_ATTEMPTS = 3;
const WS_RECONNECT_BASE_DELAY_MS = 2000;

function normalizeLiveModel(model: string): string {
  return model.startsWith('models/') ? model : `models/${model}`;
}

// Passive-transcription system instruction. The Live API always produces a
// model turn, but for meeting transcription we never want the model to act as
// a conversational participant — we only consume `inputTranscription`. The
// configured live model (gemini-3.1-flash-live-preview) is native-audio-only
// and rejects TEXT as a response modality, so we request AUDIO here and
// simply discard the model's spoken reply on the client; the follow-up
// questions shown to the expert are generated by a separate backend step,
// not here. Note the real setup is locked server-side into the ephemeral
// token (see qinterrogator_manager._build_live_connect_setup) — this message
// just needs to match so setup doesn't get rejected by the Constrained endpoint.
const LIVE_TRANSCRIBER_SYSTEM_INSTRUCTION =
  'You are a passive transcription engine for a live expert interview. ' +
  'Do not answer, do not ask questions, and do not add commentary. ' +
  'Reply with a single period and nothing else.';

function buildLiveSetupMessage(model: string) {
  return {
    setup: {
      model: normalizeLiveModel(model),
      generationConfig: {
        responseModalities: ['AUDIO'],
      },
      inputAudioTranscription: {},
      systemInstruction: {
        parts: [{ text: LIVE_TRANSCRIBER_SYSTEM_INSTRUCTION }],
      },
    },
  };
}

function liveSetupErrorMessage(data: Record<string, unknown>): string | null {
  const err = data.error;
  if (typeof err === 'string') return err;
  if (err && typeof err === 'object') {
    const msg = (err as { message?: string }).message;
    if (msg) return msg;
  }
  return null;
}

function microphoneErrorMessage(err: unknown): string {
  if (err instanceof DOMException) {
    if (err.name === 'NotAllowedError') {
      return (
        'Microphone access was denied. In Firefox, click the permissions icon in the ' +
        'address bar and allow microphone access for this site, then try again.'
      );
    }
    if (err.name === 'NotFoundError') {
      return 'No microphone was found. Connect a mic and try again.';
    }
    if (err.name === 'NotReadableError') {
      return 'Microphone is in use by another application.';
    }
    if (err.name === 'SecurityError' || !window.isSecureContext) {
      return 'Microphone access requires HTTPS or localhost.';
    }
    return err.message || 'Microphone access failed.';
  }
  if (err instanceof Error) {
    return err.message;
  }
  return 'Failed to start live transcription';
}

export function useLiveTranscription({
  sessionId,
  auth,
  enabled,
  onSegmentSubmitted,
  onError,
}: UseLiveTranscriptionOptions) {
  const [isListening, setIsListening] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isReconnecting, setIsReconnecting] = useState(false);
  const [liveLines, setLiveLines] = useState<LiveTranscriptLine[]>([]);
  const [partialText, setPartialText] = useState('');
  const [activeSpeakerLabel, setActiveSpeakerLabel] = useState('unknown');
  const [speakerLabels, setSpeakerLabels] = useState<Record<string, string>>({});

  const wsRef = useRef<WebSocket | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const processorRef = useRef<ScriptProcessorNode | null>(null);
  const pendingTextRef = useRef('');
  const segmentStartRef = useRef<string | null>(null);
  const pcmRingRef = useRef<ArrayBuffer[]>([]);
  const pcmRingBytesRef = useRef(0);
  const lastDiarizeRef = useRef<number>(0);
  const segmentCounterRef = useRef(0);
  const speakerHintRef = useRef('unknown');
  const setupCompleteRef = useRef<(() => void) | null>(null);
  const setupRejectRef = useRef<((err: Error) => void) | null>(null);
  const canSendAudioRef = useRef(false);
  const userInitiatedStopRef = useRef(false);
  const reconnectAttemptsRef = useRef(0);
  const reconnectTimerRef = useRef<number | null>(null);
  const enabledRef = useRef(enabled);
  const sessionIdRef = useRef(sessionId);

  // ChatPage passes inline callbacks and `auth` is a context value that changes
  // identity across renders. Keep them in refs so the memoized callbacks below
  // (and therefore `stopListening`) stay referentially stable — otherwise the
  // unmount cleanup effect would tear down the active WebSocket on every render.
  const authRef = useRef(auth);
  const onSegmentSubmittedRef = useRef(onSegmentSubmitted);
  const onErrorRef = useRef(onError);
  const stopListeningRef = useRef<(() => Promise<void>) | null>(null);
  const startListeningRef = useRef<(() => Promise<boolean>) | null>(null);
  useEffect(() => {
    authRef.current = auth;
    onSegmentSubmittedRef.current = onSegmentSubmitted;
    onErrorRef.current = onError;
    enabledRef.current = enabled;
    sessionIdRef.current = sessionId;
  }, [auth, enabled, onError, onSegmentSubmitted, sessionId]);

  const appendPcmToRing = useCallback((pcm: ArrayBuffer) => {
    pcmRingRef.current.push(pcm);
    pcmRingBytesRef.current += pcm.byteLength;
    const maxBytes = PCM_RING_MAX_SECONDS * TARGET_SAMPLE_RATE * 2;
    while (pcmRingBytesRef.current > maxBytes && pcmRingRef.current.length > 0) {
      const removed = pcmRingRef.current.shift();
      if (removed) {
        pcmRingBytesRef.current -= removed.byteLength;
      }
    }
  }, []);

  const flushSegment = useCallback(async (text: string, speakerLabel: string) => {
    const trimmed = text.trim();
    if (!trimmed || !sessionId) return;

    const endedAt = new Date().toISOString();
    const startedAt = segmentStartRef.current || endedAt;
    segmentStartRef.current = null;
    pendingTextRef.current = '';
    setPartialText('');

    const lineId = `live-${Date.now()}-${segmentCounterRef.current}`;
    segmentCounterRef.current += 1;
    setLiveLines((prev) => [
      ...prev,
      {
        id: lineId,
        text: trimmed,
        speakerLabel,
        isFinal: true,
        startedAt,
        endedAt,
      },
    ]);

    try {
      const response = await submitTranscriptSegment(
        sessionId,
        {
          segment_id: lineId,
          text: trimmed,
          started_at: startedAt,
          ended_at: endedAt,
          is_final: true,
          speaker_label: speakerLabel,
          source: 'live',
        },
        authRef.current
      );
      onSegmentSubmittedRef.current?.(response);
    } catch (err) {
      onErrorRef.current?.(err instanceof Error ? err.message : 'Failed to submit transcript segment');
    }
  }, [sessionId]);

  const maybeRunDiarization = useCallback(async () => {
    if (!sessionId || pcmRingRef.current.length === 0) return;
    const now = Date.now();
    if (now - lastDiarizeRef.current < DIARIZE_INTERVAL_MS) return;
    const minBytes = 10 * TARGET_SAMPLE_RATE * 2;
    if (pcmRingBytesRef.current < minBytes) return;

    lastDiarizeRef.current = now;
    const pcmMerged = concatArrayBuffers([...pcmRingRef.current]);
    const pcmBase64 = arrayBufferToBase64(pcmMerged);

    try {
      const result = await diarizeAudio(sessionId, pcmBase64, authRef.current);
      const segments = result.segments || [];
      if (segments.length > 0) {
        const last = segments[segments.length - 1];
        if (last?.speaker) {
          speakerHintRef.current = last.speaker;
        }
      }
    } catch (err) {
      console.warn('[LiveTranscription] diarization failed:', err);
    }
  }, [sessionId]);

  // The Live API sends control messages (e.g. setupComplete) as text frames
  // but content messages (which may carry inline audio bytes now that we run
  // AUDIO response modality) as binary frames. With the default `binaryType`
  // of "blob", `raw.data` for those is a Blob, and `String(blob)` stringifies
  // to "[object Blob]" — not JSON — which is what caused the parse errors.
  // `ws.binaryType` is set to "arraybuffer" in connectLiveWebSocket so binary
  // frames arrive as an ArrayBuffer we can decode synchronously here.
  const decodeServerMessageData = (data: unknown): string | null => {
    if (typeof data === 'string') return data;
    if (data instanceof ArrayBuffer) return new TextDecoder('utf-8').decode(data);
    return null;
  };

  const handleServerMessage = useCallback((raw: MessageEvent) => {
    try {
      const text = decodeServerMessageData(raw.data);
      if (text === null) {
        console.warn('[LiveTranscription] unsupported ws message data type', raw.data);
        return;
      }
      const data = JSON.parse(text) as Record<string, unknown>;
      if (process.env.NODE_ENV === 'development') {
        console.debug('[LiveTranscription] ws message', data);
      }

      const setupError = liveSetupErrorMessage(data);
      if (setupError) {
        setupRejectRef.current?.(new Error(setupError));
        setupRejectRef.current = null;
        return;
      }

      if ('setupComplete' in data) {
        canSendAudioRef.current = true;
        setupCompleteRef.current?.();
        setupCompleteRef.current = null;
        setupRejectRef.current = null;
        return;
      }

      const serverContent = data.serverContent as {
        inputTranscription?: { text?: string };
        turnComplete?: boolean;
        interrupted?: boolean;
      } | undefined;
      if (!serverContent) return;

      if (serverContent.inputTranscription?.text) {
        const chunk = String(serverContent.inputTranscription.text);
        if (!segmentStartRef.current) {
          segmentStartRef.current = new Date().toISOString();
        }
        pendingTextRef.current = `${pendingTextRef.current}${chunk}`;
        setActiveSpeakerLabel(speakerHintRef.current);
        setPartialText(pendingTextRef.current);
      }

      if (serverContent.turnComplete || serverContent.interrupted) {
        const text = pendingTextRef.current;
        if (text.trim()) {
          void flushSegment(text, speakerHintRef.current);
        }
      }
    } catch (err) {
      console.warn('[LiveTranscription] failed to parse server message', err);
    }
  }, [flushSegment]);

  const clearReconnectTimer = useCallback(() => {
    if (reconnectTimerRef.current !== null) {
      window.clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
  }, []);

  const scheduleReconnect = useCallback(() => {
    if (userInitiatedStopRef.current || !enabledRef.current || !sessionIdRef.current) {
      return;
    }
    if (reconnectAttemptsRef.current >= MAX_WS_RECONNECT_ATTEMPTS) {
      onErrorRef.current?.(
        'Live transcription disconnected. Resume recording to continue capturing speech.'
      );
      setIsReconnecting(false);
      return;
    }

    reconnectAttemptsRef.current += 1;
    setIsReconnecting(true);
    const delay = WS_RECONNECT_BASE_DELAY_MS * reconnectAttemptsRef.current;
    clearReconnectTimer();
    reconnectTimerRef.current = window.setTimeout(() => {
      reconnectTimerRef.current = null;
      void startListeningRef.current?.();
    }, delay);
  }, [clearReconnectTimer]);

  const stopListening = useCallback(async () => {
    userInitiatedStopRef.current = true;
    clearReconnectTimer();
    setIsReconnecting(false);
    reconnectAttemptsRef.current = 0;
    setIsListening(false);
    setIsConnecting(false);
    canSendAudioRef.current = false;
    setupCompleteRef.current = null;
    setupRejectRef.current = null;

    if (pendingTextRef.current.trim()) {
      await flushSegment(pendingTextRef.current, speakerHintRef.current);
    }

    processorRef.current?.disconnect();
    processorRef.current = null;
    audioContextRef.current?.close();
    audioContextRef.current = null;
    mediaStreamRef.current?.getTracks().forEach((t) => t.stop());
    mediaStreamRef.current = null;

    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.close();
    }
    wsRef.current = null;
  }, [clearReconnectTimer, flushSegment]);

  stopListeningRef.current = stopListening;

  const connectLiveWebSocket = useCallback(async (): Promise<boolean> => {
    const activeSessionId = sessionIdRef.current;
    if (!activeSessionId) return false;

    const stream = mediaStreamRef.current;
    if (!stream) {
      return false;
    }

    const tokenData = await mintLiveToken(activeSessionId, authRef.current);
    const wsUrl = `${tokenData.ws_url}?access_token=${encodeURIComponent(tokenData.token)}`;
    const ws = new WebSocket(wsUrl);
    ws.binaryType = 'arraybuffer';
    wsRef.current = ws;
    ws.onmessage = handleServerMessage;

    const setupCompletePromise = new Promise<void>((resolve, reject) => {
      const timeout = window.setTimeout(() => {
        reject(new Error(
          'Gemini Live setup timed out. Open the browser console (Network → WS) to inspect messages.'
        ));
      }, LIVE_SETUP_TIMEOUT_MS);
      setupCompleteRef.current = () => {
        window.clearTimeout(timeout);
        resolve();
      };
      setupRejectRef.current = (err: Error) => {
        window.clearTimeout(timeout);
        reject(err);
      };
    });

    await new Promise<void>((resolve, reject) => {
      ws.onopen = () => resolve();
      ws.onerror = () => reject(new Error('WebSocket connection failed'));
      ws.onclose = (event) => {
        if (process.env.NODE_ENV === 'development') {
          console.debug('[LiveTranscription] ws closed', event.code, event.reason);
        }
        if (setupRejectRef.current) {
          const reason = event.reason ? `: ${event.reason}` : '';
          setupRejectRef.current(new Error(`WebSocket closed (${event.code})${reason}`));
          setupRejectRef.current = null;
        }
        if (wsRef.current === ws) {
          canSendAudioRef.current = false;
          wsRef.current = null;
          setIsListening(false);
          if (!userInitiatedStopRef.current) {
            scheduleReconnect();
          }
        }
      };
    });

    ws.send(JSON.stringify(buildLiveSetupMessage(tokenData.model)));
    await setupCompletePromise;

    let audioContext = audioContextRef.current;
    if (!audioContext || audioContext.state === 'closed') {
      audioContext = new AudioContext();
      audioContextRef.current = audioContext;
      await audioContext.resume();
      const source = audioContext.createMediaStreamSource(stream);
      const processor = audioContext.createScriptProcessor(4096, 1, 1);
      processorRef.current = processor;
      processor.onaudioprocess = (event) => {
        const input = event.inputBuffer.getChannelData(0);
        const downsampled = downsampleBuffer(input, audioContext!.sampleRate, TARGET_SAMPLE_RATE);
        const pcm = floatTo16BitPCM(downsampled);
        appendPcmToRing(pcm);

        const activeWs = wsRef.current;
        if (canSendAudioRef.current && activeWs && activeWs.readyState === WebSocket.OPEN) {
          activeWs.send(JSON.stringify({
            realtimeInput: {
              audio: {
                mimeType: 'audio/pcm;rate=16000',
                data: arrayBufferToBase64(pcm),
              },
            },
          }));
        }
        void maybeRunDiarization();
      };
      source.connect(processor);
      processor.connect(audioContext.destination);
    }

    reconnectAttemptsRef.current = 0;
    setIsReconnecting(false);
    setIsListening(true);
    return true;
  }, [appendPcmToRing, handleServerMessage, maybeRunDiarization, scheduleReconnect]);

  const startListening = useCallback(async (): Promise<boolean> => {
    if (!sessionId || !enabled) return false;
    userInitiatedStopRef.current = false;
    clearReconnectTimer();
    setIsConnecting(true);
    setPartialText('');
    pendingTextRef.current = '';
    segmentStartRef.current = null;
    lastDiarizeRef.current = Date.now();

    try {
      if (!window.isSecureContext) {
        throw new Error('Microphone access requires HTTPS or localhost.');
      }
      if (!navigator.mediaDevices?.getUserMedia) {
        throw new Error('This browser does not support microphone capture.');
      }

      if (!mediaStreamRef.current) {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        mediaStreamRef.current = stream;
      }

      return await connectLiveWebSocket();
    } catch (err) {
      onErrorRef.current?.(microphoneErrorMessage(err));
      await stopListening();
      return false;
    } finally {
      setIsConnecting(false);
    }
  }, [
    clearReconnectTimer,
    connectLiveWebSocket,
    enabled,
    sessionId,
    stopListening,
  ]);

  startListeningRef.current = startListening;

  // Tear down only on real unmount. `stopListening` is read through a ref so a
  // changed identity never re-triggers this cleanup mid-session.
  useEffect(() => {
    return () => {
      void stopListeningRef.current?.();
    };
  }, []);

  const finishSession = useCallback(async () => {
    userInitiatedStopRef.current = true;
    await stopListening();
    if (!sessionId) return;
    try {
      await submitTranscriptSegment(
        sessionId,
        {
          segment_id: `finish-${Date.now()}`,
          text: 'finish',
          is_final: true,
          speaker_label: 'user',
          source: 'live',
        },
        authRef.current
      );
    } catch (err) {
      onErrorRef.current?.(err instanceof Error ? err.message : 'Failed to finish session');
    }
  }, [sessionId, stopListening]);

  return {
    isListening,
    isConnecting,
    isReconnecting,
    liveLines,
    partialText,
    activeSpeakerLabel,
    speakerLabels,
    setSpeakerLabels,
    startListening,
    stopListening,
    finishSession,
  };
}
